import FormComp from '../FormComp';
import styles from './SquareNewTransfer.module.css';

import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { IoCloseSharp, IoSearchSharp, IoTrashOutline } from 'react-icons/io5';

import useApi from '../../hooks/useApi';
import { useModal } from '../../contexts/ModalContext';
import Overlay from '../Overlay';
import Modal from '../Utils/Modal';
import SmallLoading from '../Utils/SmallLoading';
import { formatCPF } from '../../Funcoes/FormatCPF';

/* Modal de criação de transferência. Suporta múltiplos colaboradores:
   o usuário preenche os dados gerais (CC origem/destino, solicitante,
   previsão), informa uma matrícula no campo abaixo, clica na lupa para
   buscar o colaborador ATIVO no Protheus e o adiciona à lista que será
   transferida. Cada colab vira uma linha em `transferencias`, mas todos
   compartilham o mesmo cabeçalho e disparam UM ÚNICO e-mail. */

const SquareNewTransfer = ({ getNewValues, obras, lideres, onClose }) => {

    const api = useApi();
    const { closeModal } = useModal();

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            closeModal();
        }
    };

    const [formValues, setFormValues] = useState({
        ccOrigem: '',
        ccDestino: '',
        prevCheg: '',
        solicitante: '',
        colaboradores: [],
        matriculaInput: ''
    });

    const [foundColab, setFoundColab] = useState(null);
    const [searchError, setSearchError] = useState('');
    const [searching, setSearching] = useState(false);
    const [savingItem, setSavingItem] = useState(false);

    const handleChange = (value, name) => {
        setFormValues((prev) => ({
            ...prev,
            [name]: value
        }));

        /* Editar a matrícula limpa o card de busca anterior. */
        if (name === 'matriculaInput') {
            if (foundColab) setFoundColab(null);
            if (searchError) setSearchError('');
        }
    };

    /* Busca o colaborador no Protheus pela matrícula digitada. */
    const handleSearchColab = async () => {

        const mat = (formValues.matriculaInput || '').trim();
        if (mat.length !== 6) {
            toast.warning('Informe uma matrícula de 6 dígitos.');
            return;
        }

        const jaAdicionado = formValues.colaboradores.some((c) => c.matricula === mat);
        if (jaAdicionado) {
            setFoundColab(null);
            setSearchError(`Colaborador ${mat} já está na lista.`);
            return;
        }

        setSearching(true);
        setFoundColab(null);
        setSearchError('');

        try {

            const { data } = await api.get(
                '/Painel/API/Transferencia/GetColabByMatricula/',
                { params: { matricula: mat } }
            );

            setFoundColab({
                matricula: data.MATRICULA,
                nome: data.NOME,
                cpf: data.CPF,
                funcao: data.FUNCAO,
                centroCusto: data.CENTRO_CUSTO
            });

        } catch (error) {

            console.log(error);
            const status = error?.response?.status;
            const msg =
                status === 404
                    ? 'Colaborador não encontrado ou inativo.'
                    : 'Falha ao buscar colaborador. Tente novamente.';
            setSearchError(msg);

        } finally {

            setSearching(false);

        }

    };

    /* Adiciona o colab encontrado à lista que será transferida e
       limpa o estado de busca para o próximo lookup. */
    const handleAddFoundColab = () => {
        if (!foundColab) return;

        const dup = formValues.colaboradores.some(
            (c) => c.matricula === foundColab.matricula
        );
        if (dup) {
            toast.warning('Colaborador já está na lista.');
            return;
        }

        setFormValues((prev) => ({
            ...prev,
            colaboradores: [...prev.colaboradores, foundColab],
            matriculaInput: ''
        }));
        setFoundColab(null);
        setSearchError('');
    };

    const handleRemoveColab = (matricula) => {
        setFormValues((prev) => ({
            ...prev,
            colaboradores: prev.colaboradores.filter((c) => c.matricula !== matricula)
        }));
    };

    const handleCancelFound = () => {
        setFoundColab(null);
        setSearchError('');
    };

    /* Validações para habilitar SALVAR. */
    const validation = useMemo(() => {
        const errors = [];

        if (!formValues.ccOrigem) errors.push('CC ORIGEM');
        if (!formValues.ccDestino) errors.push('CC DESTINO');
        if (!formValues.solicitante) errors.push('SOLICITANTE');
        if (!formValues.prevCheg) errors.push('PREV. CHEGADA');
        if (!formValues.colaboradores || formValues.colaboradores.length === 0) {
            errors.push('COLABORADORES');
        }

        if (
            formValues.ccOrigem &&
            formValues.ccDestino &&
            formValues.ccOrigem === formValues.ccDestino
        ) {
            errors.push('CC ORIGEM e CC DESTINO devem ser diferentes');
        }

        return {
            errors,
            valido: errors.length === 0
        };
    }, [formValues]);

    const matriculaValida = (formValues.matriculaInput || '').trim().length === 6;

    const handleSubmit = async (e) => {

        e.preventDefault();

        if (!validation.valido) {
            toast.warning('Preencha todos os campos antes de salvar.');
            return;
        }

        setSavingItem(true);

        try {

            const payload = {
                ccOrigem: formValues.ccOrigem,
                ccDestino: formValues.ccDestino,
                prevCheg: formValues.prevCheg,
                solicitante: formValues.solicitante,
                colaboradores: formValues.colaboradores.map((c) => ({
                    matricula: c.matricula,
                    nome: c.nome,
                    cpf: c.cpf,
                    funcao: c.funcao
                }))
            };

            await toast.promise(
                api.post('/Painel/API/SaveTransfer/', payload),
                {
                    pending: 'SALVANDO TRANSFERÊNCIA',
                    success: 'TRANSFERÊNCIA REGISTRADA COM SUCESSO',
                    error: 'FALHA AO SALVAR TRANSFERÊNCIA'
                }
            );

            await getNewValues();
            handleClose();

        } catch (error) {

            console.log(error);

        } finally {

            setSavingItem(false);

        }

    };

    return (
        <Overlay state={true}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h3>NOVA TRANSFERÊNCIA</h3>
                    <div className={styles.closeBtn} onClick={handleClose}>
                        <IoCloseSharp />
                    </div>
                </div>

                <div className={styles.formContainer}>
                    <form onSubmit={handleSubmit}>
                        <FormComp
                            title="CC ORIGEM"
                            type="select"
                            options={obras}
                            valor={formValues.ccOrigem}
                            changeFunc={(e) => handleChange(e.target.value, 'ccOrigem')}
                            disabled={savingItem}
                        />
                        <FormComp
                            title="CC DESTINO"
                            type="select"
                            options={obras}
                            valor={formValues.ccDestino}
                            changeFunc={(e) => handleChange(e.target.value, 'ccDestino')}
                            disabled={savingItem}
                        />
                        <FormComp
                            title="SOLICITANTE"
                            type="select"
                            obj
                            options={lideres ?? []}
                            valor={formValues.solicitante}
                            changeFunc={(e) => handleChange(e.target.value, 'solicitante')}
                            disabled={savingItem}
                        />
                        <FormComp
                            title="PREV. CHEGADA"
                            type="date"
                            valor={formValues.prevCheg}
                            changeFunc={(e) => handleChange(e.target.value, 'prevCheg')}
                            disabled={savingItem}
                        />

                        {/* Linha de busca: input + lupa. Permite encontrar
                            o colaborador ativo no Protheus e adicioná-lo
                            à lista de transferidos. */}
                        <div className={styles.matriculaRow}>
                            <FormComp
                                title="MATRÍCULA DO COLABORADOR"
                                type="text"
                                valor={formValues.matriculaInput}
                                changeFunc={(e) =>
                                    handleChange(
                                        e.target.value.replace(/\D/g, '').slice(0, 6),
                                        'matriculaInput'
                                    )
                                }
                                maxLength={6}
                                placeholder="DIGITE A MATRÍCULA"
                                disabled={savingItem || searching}
                            />
                            <button
                                type="button"
                                className={styles.searchBtn}
                                onClick={handleSearchColab}
                                disabled={!matriculaValida || searching || savingItem}
                                title="Buscar colaborador"
                            >
                                <IoSearchSharp />
                            </button>
                        </div>

                        {searching && (
                            <div className={styles.errorMsg} style={{
                                background: '#f8fafc',
                                borderColor: '#dce3ec',
                                color: '#64748b'
                            }}>
                                Buscando colaborador...
                            </div>
                        )}

                        {searchError && !searching && (
                            <div className={styles.errorMsg}>
                                {searchError}
                            </div>
                        )}

                        {foundColab && !searching && (
                            <div className={styles.foundCard}>
                                <p><strong>NOME:</strong> {foundColab.nome}</p>
                                <p><strong>MATRÍCULA:</strong> {foundColab.matricula}</p>
                                <p><strong>CPF:</strong> {formatCPF(foundColab.cpf)}</p>
                                <p><strong>FUNÇÃO:</strong> {foundColab.funcao}</p>
                                {foundColab.centroCusto && (
                                    <p><strong>CC ATUAL:</strong> {foundColab.centroCusto}</p>
                                )}
                                <div className={styles.foundActions}>
                                    <button
                                        type="button"
                                        className={styles.addBtn}
                                        onClick={handleAddFoundColab}
                                        disabled={savingItem}
                                    >
                                        ADICIONAR À LISTA
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.cancelFoundBtn}
                                        onClick={handleCancelFound}
                                    >
                                        CANCELAR
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Lista de colaboradores já adicionados. */}
                        <div className={styles.colabsList}>
                            <div className={styles.listHeader}>
                                COLABORADORES A TRANSFERIR (
                                {formValues.colaboradores.length})
                            </div>
                            {formValues.colaboradores.length === 0 ? (
                                <p className={styles.emptyList}>
                                    Nenhum colaborador adicionado ainda.
                                </p>
                            ) : (
                                formValues.colaboradores.map((c) => (
                                    <div key={c.matricula} className={styles.colabRow}>
                                        <span>
                                            {c.nome} — {c.matricula} ({c.funcao})
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.removeBtn}
                                            onClick={() => handleRemoveColab(c.matricula)}
                                            disabled={savingItem}
                                            title="Remover da lista"
                                        >
                                            <IoTrashOutline />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        <button
                            type="submit"
                            className={styles.saveBtn}
                            disabled={savingItem || !validation.valido}
                        >
                            SALVAR
                        </button>
                    </form>
                </div>

                {savingItem && (
                    <Overlay state={true}>
                        <Modal size="sm">
                            <SmallLoading />
                        </Modal>
                    </Overlay>
                )}
            </div>
        </Overlay>
    );

};

export default SquareNewTransfer;
